import React, { useState } from 'react';
import FileUpload from './FileUpload';
import YouTubeVideoInput from './YouTubeVideoInput';
import EditItemModal from './EditItemModal';
import ImagePositionEditor from './ImagePositionEditor';
import { getMediaUrl, normalizeGalleryItem } from '../../utils/mediaUrl';
import './FileUpload.css';

const GalleryManager = ({ value = [], onChange, type = 'mixed' }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  
  const handleFileUpload = (fileData) => {
    // Create gallery item from uploaded file (images only)
    const galleryItem = normalizeGalleryItem({
      id: fileData.uuid || fileData.id || Date.now().toString(),
      title: (fileData.original_name || fileData.name || 'Photo').replace(/\.[^/.]+$/, ""),
      type: fileData.type || fileData.file_type || 'image',
      icon: 'fas fa-camera',
      ...fileData,
    });
    
    const newValue = [...value, galleryItem];
    onChange(newValue);
  };

  const handleYouTubeVideoAdd = (videoData) => {
    // YouTube video is already formatted by YouTubeVideoInput
    const newValue = [...value, videoData];
    onChange(newValue);
  };
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleItemUpdate = (index, field, newValue) => {
    const updatedItems = [...value];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: newValue
    };
    onChange(updatedItems);
  };

  const handleReplaceMedia = (index, fileData) => {
    const updatedItems = [...value];
    const existingItem = updatedItems[index];
    
    // Create new gallery item from uploaded file, preserving the title
    const newItem = normalizeGalleryItem({
      id: existingItem.id || fileData.uuid || fileData.id || Date.now().toString(),
      title: existingItem.title || (fileData.original_name || fileData.name || 'Photo').replace(/\.[^/.]+$/, ""),
      type: fileData.type || fileData.file_type || 'image',
      icon: existingItem.icon || 'fas fa-camera',
      ...fileData,
    });

    updatedItems[index] = newItem;
    onChange(updatedItems);
  };

  const isLegacyItem = (item) => {
    // Legacy items only have title, icon - no resolvable media URL
    return !getMediaUrl(item) && !item.youtube_id;
  };

  const handleEditItem = (index) => {
    setEditingItem({ item: value[index], index });
  };

  const handleSaveEditedItem = (index, editedItem) => {
    const updatedItems = [...value];
    updatedItems[index] = editedItem;
    onChange(updatedItems);
    setEditingItem(null);
  };

  const handleCloseEditModal = () => {
    setEditingItem(null);
  };

  const handleImagePositionSave = (index, settings) => {
    console.log('💾 Saving image position for index:', index, settings);
    const updatedItems = [...value];
    updatedItems[index] = {
      ...updatedItems[index],
      imageSettings: settings
    };
    onChange(updatedItems);
    // Don't close immediately - let user close manually with button or X
    console.log('✅ Image settings saved, editor stays open');
  };
  
  const handleItemDelete = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) {
      const updatedItems = value.filter((_, i) => i !== index);
      onChange(updatedItems);
    }
  };
  
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedItems.length} élément(s) ?`)) {
      const updatedItems = value.filter((_, index) => !selectedItems.includes(index));
      onChange(updatedItems);
      setSelectedItems([]);
    }
  };
  
  const toggleItemSelection = (index) => {
    setSelectedItems(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  
  const selectAllItems = () => {
    if (selectedItems.length === value.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(value.map((_, index) => index));
    }
  };
  
  const getFileTypeFilter = () => {
    switch (type) {
      case 'photos':
        return 'image/*';
      case 'videos':
        return 'video/*';
      default:
        return 'image/*,video/*';
    }
  };
  
  const getUploadLabel = () => {
    switch (type) {
      case 'photos':
        return 'Télécharger des photos';
      case 'videos':
        return 'Télécharger des vidéos';
      default:
        return 'Télécharger des médias';
    }
  };

  return (
    <div className="gallery-manager">
      {/* Upload Section - Different inputs based on type */}
      {type === 'videos' ? (
        // YouTube Video Input for video galleries
        <YouTubeVideoInput
          onVideoAdd={handleYouTubeVideoAdd}
          label="Ajouter une vidéo YouTube"
        />
      ) : type === 'photos' ? (
        // File Upload for photo galleries
        <FileUpload
          onFileUpload={handleFileUpload}
          acceptedTypes="image/*"
          label="Télécharger des photos"
        />
      ) : (
        // Mixed gallery - both options
        <div className="mixed-gallery-inputs">
          <div className="input-tabs" style={{
            display: 'flex',
            marginBottom: '20px',
            borderBottom: '2px solid #e5e7eb'
          }}>
            <div className="tab-content">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-images"></i>
                  Télécharger des Photos
                </h4>
                <FileUpload
                  onFileUpload={handleFileUpload}
                  acceptedTypes="image/*"
                  label="Télécharger des photos"
                />
              </div>
              
              <div>
                <h4 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fab fa-youtube" style={{ color: '#ff0000' }}></i>
                  Ajouter des Vidéos YouTube
                </h4>
                <YouTubeVideoInput
                  onVideoAdd={handleYouTubeVideoAdd}
                  label="Ajouter une vidéo YouTube"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Gallery Items Management */}
      {value.length > 0 && (
        <div className="gallery-items-manager">
          <div className="gallery-items-header">
            <h4 className="gallery-items-title">Éléments de la galerie</h4>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className="gallery-items-count">
                {value.length} élément{value.length > 1 ? 's' : ''}
              </span>
              {value.length > 1 && (
                <button
                  type="button"
                  className="media-preview-btn"
                  onClick={selectAllItems}
                  title={selectedItems.length === value.length ? "Tout désélectionner" : "Tout sélectionner"}
                >
                  <i className={`fas ${selectedItems.length === value.length ? 'fa-times' : 'fa-check-square'}`}></i>
                  {selectedItems.length === value.length ? 'Désélectionner' : 'Sélectionner tout'}
                </button>
              )}
              {selectedItems.length > 0 && (
                <button
                  type="button"
                  className="media-preview-btn danger"
                  onClick={handleBulkDelete}
                  title={`Supprimer ${selectedItems.length} élément(s)`}
                >
                  <i className="fas fa-trash"></i>
                  Supprimer ({selectedItems.length})
                </button>
              )}
            </div>
          </div>
          
          <div className="gallery-items-grid">
            {value.map((item, index) => (
              <React.Fragment key={item.id || index}>
                <div 
                  className={`gallery-item-card ${selectedItems.includes(index) ? 'selected' : ''}`}
                >
                {/* Selection Checkbox */}
                <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 2 }}>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(index)}
                    onChange={() => toggleItemSelection(index)}
                    style={{ transform: 'scale(1.2)' }}
                  />
                </div>
                
                {/* Media Preview */}
                <div style={{ position: 'relative' }}>
                  {getMediaUrl(item) && (item.type === 'image' || !item.youtube_id) ? (
                    // Regular uploaded image
                    <img 
                      src={getMediaUrl(item)} 
                      alt={item.title}
                      className="gallery-item-preview"
                      style={item.imageSettings ? {
                        transform: `translate(calc(-50% + ${item.imageSettings.position.x}px), calc(-50% + ${item.imageSettings.position.y}px)) scale(${item.imageSettings.scale})`,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        minWidth: '100%',
                        minHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'cover'
                      } : {}}
                    />
                  ) : item.youtube_id && item.thumbnail ? (
                    // YouTube video with thumbnail
                    <div style={{ position: 'relative' }}>
                      <img 
                        src={item.thumbnail} 
                        alt={item.title}
                        className="gallery-item-preview"
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                        }}
                      />
                      {/* YouTube Play Button */}
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#ff0000',
                        fontSize: '2.5rem',
                        textShadow: '0 2px 4px rgba(0,0,0,0.7)',
                        cursor: 'pointer'
                      }}>
                        <i className="fab fa-youtube"></i>
                      </div>
                      {/* Duration badge */}
                      {item.duration && (
                        <div style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.8)',
                          color: 'white',
                          padding: '3px 6px',
                          borderRadius: '3px',
                          fontSize: '0.7rem',
                          fontWeight: '500'
                        }}>
                          {item.duration}
                        </div>
                      )}
                      {/* YouTube badge */}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        background: '#ff0000',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '0.6rem',
                        fontWeight: '500'
                      }}>
                        YouTube
                      </div>
                    </div>
                  ) : item.thumbnail && item.type === 'video' ? (
                    // Legacy video with thumbnail (old format)
                    <div style={{ position: 'relative' }}>
                      <img 
                        src={item.thumbnail} 
                        alt={item.title}
                        className="gallery-item-preview"
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontSize: '2rem',
                        textShadow: '0 2px 4px rgba(0,0,0,0.7)'
                      }}>
                        <i className="fas fa-play-circle"></i>
                      </div>
                      {item.duration && (
                        <div style={{
                          position: 'absolute',
                          bottom: '5px',
                          right: '5px',
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '0.7rem'
                        }}>
                          {item.duration}
                        </div>
                      )}
                    </div>
                  ) : (
                    // Placeholder for items without media
                    <div 
                      className="gallery-item-preview"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280'
                      }}
                    >
                      <i className={item.icon || 'fas fa-file'} style={{ fontSize: '2rem' }}></i>
                    </div>
                  )}
                </div>
                
                {/* Item Info */}
                <div className="gallery-item-info">
                  <input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => handleItemUpdate(index, 'title', e.target.value)}
                    className="gallery-item-title"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      width: '100%',
                      fontSize: '0.9rem',
                      fontWeight: '500'
                    }}
                    placeholder="Titre de l'élément"
                  />
                  
                  {/* Size warning for large videos */}
                  {item.sizeWarning && (
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#f59e0b',
                      backgroundColor: '#fef3c7',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      marginBottom: '0.5rem',
                      textAlign: 'center'
                    }}>
                      {item.sizeWarning}
                    </div>
                  )}
                  
                  {/* Optimization status */}
                  {item.optimized && (
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#3b82f6',
                      backgroundColor: '#dbeafe',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      marginBottom: '0.5rem',
                      textAlign: 'center'
                    }}>
                      📁 Vidéo optimisée pour le stockage
                    </div>
                  )}
                  
                  <div className="gallery-item-meta">
                    <div className="gallery-item-type">
                      <i className={`${item.youtube_id ? 'fab fa-youtube' : item.type === 'video' ? 'fas fa-video' : 'fas fa-image'}`}></i>
                      <span>
                        {item.youtube_id ? 'YouTube' : item.type === 'video' ? 'Vidéo' : 'Image'}
                        {item.type === 'video' && item.duration && ` (${item.duration})`}
                      </span>
                    </div>
                  </div>
                  
                  {/* Action buttons section */}
                  <div style={{
                    marginTop: '0.75rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => handleEditItem(index)}
                        title="Modifier cet élément"
                        style={{
                          flex: 1,
                          padding: '0.6rem',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.4rem',
                          transition: 'all 0.2s'
                        }}
                      >
                        <i className="fas fa-edit"></i>
                        <span>Modifier</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleItemDelete(index)}
                        title="Supprimer cet élément"
                        style={{
                          flex: 1,
                          padding: '0.6rem',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.4rem',
                          transition: 'all 0.2s'
                        }}
                      >
                        <i className="fas fa-trash"></i>
                        <span>Supprimer</span>
                      </button>
                    </div>
                  
                  {/* Image Position Editor for photos */}
                  {getMediaUrl(item) && item.type === 'image' && (
                    <button
                      type="button"
                      onClick={() => setEditingImageIndex(editingImageIndex === index ? null : index)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: editingImageIndex === index ? '#28a745' : '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
                      }}
                    >
                      <i className={`fas fa-${editingImageIndex === index ? 'check' : 'sliders-h'}`}></i>
                      {editingImageIndex === index ? 'En cours d\'édition...' : 'Ajuster position et zoom'}
                    </button>
                  )}
                  </div>
                </div>
              </div>
              
              {/* Full-width Image Position Editor - Outside the card */}
              {getMediaUrl(item) && item.type === 'image' && editingImageIndex === index && (
                <div style={{
                  gridColumn: '1 / -1',
                  background: 'white',
                  border: '2px solid #667eea',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginTop: '-10px',
                  marginBottom: '15px',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.2)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    paddingBottom: '1rem',
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    <h3 style={{
                      margin: 0,
                      color: '#667eea',
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <i className="fas fa-sliders-h"></i>
                      Édition: {item.title}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setEditingImageIndex(null)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <i className="fas fa-times"></i>
                      Fermer
                    </button>
                  </div>
                  <ImagePositionEditor
                    imageUrl={getMediaUrl(item)}
                    currentSettings={item.imageSettings}
                    onSave={(settings) => {
                      handleImagePositionSave(index, settings);
                      setEditingImageIndex(null);
                    }}
                  />
                </div>
              )}
            </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <EditItemModal
          item={editingItem.item}
          itemIndex={editingItem.index}
          onSave={handleSaveEditedItem}
          onClose={handleCloseEditModal}
          galleryType={type}
        />
      )}
    </div>
  );
};

export default GalleryManager;
